import { Drawer } from '@lume/ui-components';
import { useState } from 'react';

import {
  useReconciliationFlags,
  useResolveReconciliationFlag,
} from '../hooks/use-integration-reconciliation';

import {
  FLAG_TYPE_DETAIL,
  RESOLUTION_LABEL,
  flagTypeLabel,
  fmtDateTime,
} from './reconciliation-labels';

import type { FlagResolution, ReconciliationFlagWire } from '../types';

export interface MismatchDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  canManage: boolean;
  /** externalSystemId → displayName (das métricas) para rotular o connector. */
  connectorNames: Record<string, string>;
}

const RESOLUTIONS: readonly FlagResolution[] = ['accept_remote', 'keep_local', 'request_info'];

/**
 * A4 REVAMP — MismatchDrawer (Brief 5 Fatia 3a · #314).
 *
 * Lista divergências (`GET .../reconciliation-flags`) detectadas pelo
 * reconciliation. Cada flag pendente (modo manage) pode ser resolvida com uma das
 * decisões do `ResolveReconciliationFlagDto` (accept_remote/keep_local/request_info)
 * + nota opcional. Resolver emite audit `high` no backend (Policy ADR-120: não
 * auto-resolve). Reusa o Drawer do DS (`@lume/ui-components`), como B6.
 */
export function MismatchDrawer({
  isOpen,
  onClose,
  canManage,
  connectorNames,
}: MismatchDrawerProps): JSX.Element {
  const { data: flags, isLoading, error } = useReconciliationFlags(isOpen);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Divergências de reconciliation" width="lg">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-text-secondary">
          Comparação entre o registro local do Lume e o connector remoto. Cada decisão é registrada
          em auditoria — não há resolução automática.
        </p>

        {isLoading && <p className="text-sm text-text-secondary">Carregando divergências…</p>}
        {error != null && (
          <p className="text-sm text-red-600">Falha ao carregar divergências: {error.message}</p>
        )}
        {!isLoading && error == null && (flags === undefined || flags.length === 0) && (
          <div className="rounded-lg border border-dashed border-border-subtle bg-surface p-8 text-center text-sm text-text-secondary">
            Nenhuma divergência pendente. Tudo sincronizado. ✓
          </div>
        )}
        {!isLoading &&
          error == null &&
          flags?.map((flag) => (
            <MismatchRow
              key={flag.id}
              flag={flag}
              canManage={canManage}
              connectorName={connectorNames[flag.externalSystemId] ?? flag.externalSystemId}
            />
          ))}
      </div>
    </Drawer>
  );
}

interface MismatchRowProps {
  flag: ReconciliationFlagWire;
  canManage: boolean;
  connectorName: string;
}

function MismatchRow({ flag, canManage, connectorName }: MismatchRowProps): JSX.Element {
  const resolve = useResolveReconciliationFlag();
  const [note, setNote] = useState('');
  const isResolved = flag.status === 'resolved';

  const onResolve = (resolution: FlagResolution): void => {
    resolve.mutate({
      id: flag.id,
      input: { resolution, ...(note.trim() !== '' && { note: note.trim() }) },
    });
  };

  return (
    <div className="rounded-lg border border-border-subtle bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-text-primary">{flagTypeLabel(flag.flagType)}</p>
          <p className="mt-0.5 text-xs text-text-secondary">{connectorName}</p>
        </div>
        <span
          className={`rounded px-2 py-0.5 text-xs ${
            isResolved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
          }`}
        >
          {isResolved ? 'Resolvida' : 'Pendente'}
        </span>
      </div>

      {flag.tool !== null && (
        <p className="mt-2 font-mono text-xs text-text-primary">tool: {flag.tool}</p>
      )}
      <p className="mt-1 text-xs text-text-secondary">
        {FLAG_TYPE_DETAIL[flag.flagType] ?? 'Divergência detectada na reconciliation.'}
      </p>
      <p className="mt-1 text-xs text-text-secondary">
        Detectada em {fmtDateTime(flag.detectedAt)}
      </p>

      {isResolved ? (
        <p className="mt-2 text-xs text-text-secondary">
          Decisão:{' '}
          <span className="font-medium text-text-primary">
            {flag.resolution !== null ? RESOLUTION_LABEL[flag.resolution] : '—'}
          </span>{' '}
          · {fmtDateTime(flag.resolvedAt)}
          {flag.note !== null && flag.note !== '' && (
            <span className="mt-1 block italic">“{flag.note}”</span>
          )}
        </p>
      ) : (
        canManage && (
          <div className="mt-3 flex flex-col gap-2">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Nota (opcional)"
              rows={2}
              className="w-full rounded border border-border-subtle bg-surface px-2 py-1 text-xs text-text-primary"
            />
            <div className="flex flex-wrap gap-2">
              {RESOLUTIONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  disabled={resolve.isPending}
                  onClick={() => onResolve(r)}
                  className={`rounded px-3 py-1.5 text-xs ${
                    r === 'request_info'
                      ? 'border border-border-subtle text-text-primary hover:bg-bg-muted'
                      : 'bg-accent text-white hover:bg-accent-hover'
                  } disabled:opacity-50`}
                >
                  {RESOLUTION_LABEL[r]}
                </button>
              ))}
            </div>
            {resolve.error != null && (
              <p className="text-xs text-red-600">Falha ao resolver: {resolve.error.message}</p>
            )}
          </div>
        )
      )}
    </div>
  );
}
